<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import type { RouteLocationRaw } from 'vue-router'

import type { ScreenTab } from './screenTabs'

const props = defineProps<{ tabs: ScreenTab[] }>()

const route = useRoute()
const router = useRouter()

const activeName = computed(() => {
  const current = route.name
  return props.tabs.some((tab) => tab.name === current) ? current : props.tabs[0]?.name
})

function select(tab: ScreenTab) {
  if (tab.name === activeName.value) return
  /**
   * `replace`, not `push` — this is what makes path-segment tabs behave like
   * tabs. Without it, four taps between "Bus at bus stop" and "Passing route"
   * would leave four entries on the back stack and back would feel broken.
   *
   * Current params are merged so switching tabs on /stops/1670/buses lands on
   * /stops/1670/routes rather than dropping the id.
   *
   * The cast is where typed routes run out, and it costs nothing: `tab.name` is a
   * runtime union of every route name, so no single param shape can satisfy it
   * statically. The name is already checked at the point that matters — `ScreenTab.name`
   * is `keyof RouteMap`, so a typo fails to compile in `screens/tabs.ts`, where tabs are
   * declared. Merging the current params is likewise only meaningful at runtime.
   */
  router.replace({
    name: tab.name,
    params: { ...route.params, ...tab.params },
  } as RouteLocationRaw)
}
</script>

<template>
  <!--
    Rendered as navigation rather than as an ARIA tablist. Each tab is a distinct
    URL that replaces the whole screen, so there are no tabpanels for a tablist to
    own — announcing "tab" here would describe a widget that does not exist. The
    tabs are links; `aria-current` carries the selected state.
  -->
  <nav class="flex shrink-0 border-b bg-background" aria-label="Screen sections">
    <button
      v-for="tab in tabs"
      :key="tab.name"
      type="button"
      class="min-h-12 flex-1 border-b-2 px-3 py-3 text-sm transition-colors"
      :class="
        tab.name === activeName
          ? 'border-primary font-bold text-foreground'
          : 'border-transparent text-muted-foreground'
      "
      :aria-current="tab.name === activeName ? 'page' : undefined"
      @click="select(tab)"
    >
      {{ tab.label }}
    </button>
  </nav>
</template>
